

import { fetchUserInfo } from '@/entities/user/thunks/fetchUserInfo'
import { createSlice, EntityId, PayloadAction } from '@reduxjs/toolkit'

import { internalDropdowns } from '../../dropdowns/models/InternalDropdowns'
import { refreshData } from '../../schedules/thunks/refreshData'
import { UserInterfaceState } from '../model/UserInterfaceState'
import { updateActiveGroup } from '../thunks/updateActiveGroup'

export const initialUserInterfaceState: UserInterfaceState = {
  // User interface state
  activeGroupId: -1,
  currentScreen: '',
  isGlobalLoading: false,
  isRightSidebarOpen: false,
  timezoneOffset: null,

  // Stop id to fly in on the Operations' Maps
  lastSelectedStopId: null,

  // Order id to show on the Operations detailed sidebar
  detailViewedOrderId: null,

  // Order id selected on the search bar. This OrderId is used to set the initial grid table selectedModel
  selectedSearchResult: null,
}

export const userInterfaceSlice = createSlice({
  name: 'userInterface',
  initialState: initialUserInterfaceState,
  reducers: {
    setCurrentScreen: (state, action: PayloadAction<UserInterfaceState['currentScreen']>) => {
      state.currentScreen = action.payload
    },
    openRightSidebar: (state) => {
      state.isRightSidebarOpen = true
    },
    closeRightSidebar: (state) => {
      state.isRightSidebarOpen = false
    },
    setLastSelectedStopId: (state, action: PayloadAction<EntityId>) => {
      state.lastSelectedStopId = action.payload
    },
    setDetailViewedOrderId: (state, action: PayloadAction<EntityId>) => {
      state.detailViewedOrderId = action.payload
    },
    setSelectedSearchResult: (state, action: PayloadAction<UserInterfaceState['selectedSearchResult']>) => {
      state.selectedSearchResult = action.payload
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.isGlobalLoading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshData.pending, (state) => {
        state.isGlobalLoading = true
      })
      .addCase(refreshData.fulfilled, (state) => {
        state.isGlobalLoading = false
      })
      .addCase(refreshData.rejected, (state) => {
        state.isGlobalLoading = false
      })
      .addCase(updateActiveGroup.fulfilled, (state, action) => {
        state.activeGroupId = action.payload.groupId
        state.timezoneOffset = action.payload.timezoneOffset
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        let activeGroupId = state.activeGroupId
        const allTimeZones = internalDropdowns.timeZones

        // check if activeGroupId exists within the returned menus
        if (action.payload.groups) {
          const foundGroup = action.payload.groups.find((group) => group.id === activeGroupId)

          if (!foundGroup) {
            activeGroupId = -1
          }

          if ((!state.activeGroupId || activeGroupId === -1) && action.payload.defaultGroupId) {
            activeGroupId = action.payload.defaultGroupId || 0
          }

          // Update timezone offset
          const group = action.payload.groups.find((gr) => gr.id === activeGroupId)
          const timeZone = allTimeZones.find((tz) => tz.value === group?.timeZone)

          state.timezoneOffset = timeZone?.offset || null
        }

        state.activeGroupId = activeGroupId
      })
  },
})

export const {
  setCurrentScreen,
  setGlobalLoading,
  openRightSidebar,
  closeRightSidebar,
  setLastSelectedStopId,
  setDetailViewedOrderId,
  setSelectedSearchResult,
} = userInterfaceSlice.actions
